import { Watch, DoorOpen, WifiHigh, Thermometer, Pulse } from '@phosphor-icons/react';
import { api } from './api';

// Category → icon map (visual only, driven by the catalog's `category`, not by
// hardcoded type aliases — a new model in a known category gets its icon free).
const CATEGORY_ICONS = { bracelet: Watch, door_sensor: DoorOpen, gateway: WifiHigh, sensor: Thermometer };

// Session-cached device-type catalog (GET /api/device-types). This is the
// SOURCE OF TRUTH for models / categories / labels / wearable — never hardcode
// device types. A new server-side model appears in the UI with no code change.
let _cache = null;
let _promise = null;

export async function loadDeviceCatalog() {
  if (_cache) return _cache;
  if (!_promise) {
    _promise = api.get('/device-types')
      .then((r) => { _cache = Array.isArray(r.data?.device_types) ? r.data.device_types : []; return _cache; })
      .catch((e) => { _promise = null; throw e; });
  }
  return _promise;
}

// Drop the session cache so the next loadDeviceCatalog() refetches — call this
// after an admin edits a device-type label so the new label shows app-wide.
export function invalidateDeviceCatalog() {
  _cache = null;
  _promise = null;
}

export function catalogEntry(catalog, type) {
  if (!type || !Array.isArray(catalog)) return null;
  const s = String(type).toLowerCase();
  return catalog.find((d) => d.value === type)
    || catalog.find((d) => String(d.value).toLowerCase() === s)
    || null;
}

export function categoryLabel(cat) {
  const s = String(cat || '');
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
}

// Composite label: "Bracelet (W3Pro)" when the category has >1 model,
// otherwise just the model label ("Door sensor", "Gateway"). Rule is derived
// from catalog contents, not special-cased per category.
export function deviceLabelFrom(catalog, type) {
  const entry = catalogEntry(catalog, type);
  if (!entry) return type || '—';
  const siblings = (catalog || []).filter((d) => d.category === entry.category);
  if (siblings.length > 1) return `${categoryLabel(entry.category)} (${entry.label})`;
  return entry.label;
}

// Distinct categories in catalog order (for filter tabs).
export function categoriesFrom(catalog) {
  const out = [];
  for (const d of (catalog || [])) if (!out.includes(d.category)) out.push(d.category);
  return out;
}

// True if a device type is a wearable (assigns to a patient). Resolves by
// concrete model value, falling back to a category match when the backend
// sends a category name — catalog-driven, no hardcoded aliases.
export function isWearableType(catalog, type) {
  const entry = catalogEntry(catalog, type);
  if (entry) return !!entry.wearable;
  const cats = categoriesFrom(catalog);
  if (cats.includes(type)) {
    const models = (catalog || []).filter((d) => d.category === type);
    return models.length > 0 && models.every((d) => d.wearable);
  }
  return false;
}

// Icon for a device type via its catalog category. Degrades to a neutral icon
// for unknown types (e.g. a new model shipped before the catalog refreshed).
export function deviceIconFrom(catalog, type) {
  const entry = catalogEntry(catalog, type);
  return (entry && CATEGORY_ICONS[entry.category]) || Pulse;
}
